import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useApps } from '@/apps/useApps';
import { useSession } from '@/auth/useSession';
import {
  distinctCategories,
  distinctSubcategories,
  isGptApp,
  matchesCategory,
  matchesSearch,
  matchesSubcategory,
  paginate,
  totalPages as totalPagesOf,
} from '@/catalog/filter';
import { PAGE_SIZE, useCatalogState } from '@/catalog/useCatalogState';
import { AppGrid } from '@/components/catalog/AppGrid';
import { AppList } from '@/components/catalog/AppList';
import { GptGroup } from '@/components/catalog/GptGroup';
import { HiddenAppsBanner } from '@/components/catalog/HiddenAppsBanner';
import { Pagination } from '@/components/catalog/Pagination';
import { CatalogToolbar } from '@/components/catalog/Toolbar';

export function Home() {
  const { t } = useTranslation();
  const session = useSession();
  const apps = useApps();

  const catalog = useCatalogState(session.data?.entra_object_id);

  const categories = useMemo(() => distinctCategories(apps.data ?? []), [apps.data]);
  const subcategories = useMemo(
    () => distinctSubcategories(apps.data ?? [], catalog.category),
    [apps.data, catalog.category],
  );

  const filteredAll = useMemo(() => {
    if (!apps.data) return [];
    return apps.data.filter(
      (app) =>
        !catalog.hidden.has(app.slug) &&
        matchesSearch(app, catalog.search) &&
        matchesCategory(app, catalog.category) &&
        matchesSubcategory(app, catalog.subcategory),
    );
  }, [apps.data, catalog.hidden, catalog.search, catalog.category, catalog.subcategory]);

  // Los agentes GPT se agrupan en una sola card; el resto se pagina normal.
  const gptApps = useMemo(() => filteredAll.filter(isGptApp), [filteredAll]);
  const otherApps = useMemo(() => filteredAll.filter((a) => !isGptApp(a)), [filteredAll]);

  const totalPages = totalPagesOf(otherApps.length, PAGE_SIZE);
  const visiblePage = Math.min(catalog.page, totalPages);

  useEffect(() => {
    if (visiblePage !== catalog.page) catalog.setPage(visiblePage);
  }, [visiblePage, catalog]);

  const pageItems = paginate(otherApps, visiblePage, PAGE_SIZE);

  if (session.isLoading) {
    return <p className="text-slate-600">…</p>;
  }
  if (session.isError || !session.data) {
    return <p className="text-slate-600">{t('errors.session')}</p>;
  }

  const totalApps = apps.data?.length ?? 0;
  const hiddenCount = catalog.hidden.size;
  const allHidden = totalApps > 0 && totalApps === hiddenCount;

  return (
    <section aria-labelledby="home-title" className="space-y-6">
      <header>
        <h2 id="home-title" className="text-2xl font-semibold text-appsol-900">
          {t('home.greeting', { name: session.data.display_name })}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t('home.subtitle')}</p>
      </header>

      {apps.isLoading && <p className="text-sm text-slate-500">{t('home.loading')}</p>}

      {apps.isError && (
        <p role="alert" className="text-sm text-rose-700">
          {t('home.error')}
        </p>
      )}

      {apps.data && totalApps === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          {t('home.empty')}
        </p>
      )}

      {apps.data && totalApps > 0 && (
        <>
          <CatalogToolbar
            search={catalog.search}
            onSearchChange={catalog.setSearch}
            categories={categories}
            category={catalog.category}
            onCategoryChange={catalog.setCategory}
            subcategories={subcategories}
            subcategory={catalog.subcategory}
            onSubcategoryChange={catalog.setSubcategory}
            view={catalog.view}
            onViewChange={catalog.setView}
            resultCount={filteredAll.length}
          />

          <HiddenAppsBanner count={hiddenCount} onRestoreAll={catalog.restoreAll} />

          {filteredAll.length === 0 && !allHidden && (
            <p className="rounded-md border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              {t('home.noResults')}
            </p>
          )}

          {allHidden && (
            <p className="rounded-md border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              {t('home.allHidden')}
            </p>
          )}

          {gptApps.length > 0 && <GptGroup apps={gptApps} />}

          {pageItems.length > 0 && (
            catalog.view === 'cards' ? (
              <AppGrid apps={pageItems} onHide={catalog.hideApp} />
            ) : (
              <AppList apps={pageItems} onHide={catalog.hideApp} />
            )
          )}

          <Pagination page={visiblePage} totalPages={totalPages} onPageChange={catalog.setPage} />
        </>
      )}
    </section>
  );
}
